import { useState, useEffect } from "react";
import { getApplications } from "../../models/firestoreService";

const DASHBOARD_PASSWORD = "extern-ceo-2027";
const AUTH_KEY = "ea_dashboard_auth";

// ── Login ─────────────────────────────────────────────────────────────────────

function Login({ onAuth }) {
    const [password, setPassword] = useState("");
    const [error, setError] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (password === DASHBOARD_PASSWORD) {
            sessionStorage.setItem(AUTH_KEY, "1");
            onAuth();
        } else {
            setError(true);
            setPassword("");
        }
    };

    return (
        <div className="min-h-screen d-flex align-items-center justify-content-center" style={{ backgroundColor: "#0a0a0a" }}>
            <div className="w-100" style={{ maxWidth: "360px", padding: "0 1rem" }}>
                <div className="text-center mb-5">
                    <p className="fw-bold text-white mb-1" style={{ fontSize: "1.1rem" }}>Extern Academy</p>
                    <p style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.82rem" }}>CEO Dashboard</p>
                </div>
                <form onSubmit={handleSubmit}>
                    <input
                        type="password"
                        autoFocus
                        value={password}
                        onChange={e => { setPassword(e.target.value); setError(false); }}
                        placeholder="Password"
                        className="w-100 d-block mb-3"
                        style={{
                            backgroundColor: "rgba(255,255,255,0.06)",
                            border: error ? "1px solid #D23C1E" : "1px solid rgba(255,255,255,0.12)",
                            borderRadius: "8px",
                            color: "#fff",
                            padding: "12px 16px",
                            fontSize: "0.9rem",
                            outline: "none",
                        }}
                    />
                    {error && (
                        <p className="mb-3" style={{ color: "#D23C1E", fontSize: "0.8rem" }}>Incorrect password.</p>
                    )}
                    <button
                        type="submit"
                        className="w-100 fw-semibold"
                        style={{
                            backgroundColor: "#3769B9",
                            color: "#fff",
                            border: "none",
                            borderRadius: "8px",
                            padding: "12px",
                            fontSize: "0.9rem",
                            cursor: "pointer",
                        }}
                    >
                        Sign In
                    </button>
                </form>
            </div>
        </div>
    );
}

// ── Application card ──────────────────────────────────────────────────────────

function ApplicationCard({ app }) {
    const [expanded, setExpanded] = useState(false);

    const submittedAt = app.submittedAt
        ? new Date(app.submittedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
        : app.createdAt?.toDate
        ? app.createdAt.toDate().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
        : "—";

    const status = app.officiallySubmitted ? "Complete" : (app.Status || "In Progress");
    const statusColor = app.officiallySubmitted ? "#22c55e" : "#f59e0b";

    const cohort = Array.isArray(app.Cohort) ? app.Cohort.join(", ") : (app.Cohort || "—");

    return (
        <div
            className="mb-3 rounded-3"
            style={{ backgroundColor: "#111", border: "1px solid rgba(255,255,255,0.08)" }}
        >
            {/* Header row */}
            <div
                className="d-flex align-items-center justify-content-between px-4 py-3"
                style={{ cursor: "pointer" }}
                onClick={() => setExpanded(e => !e)}
            >
                <div className="d-flex align-items-center gap-4 flex-wrap">
                    <div>
                        <p className="fw-semibold text-white mb-0" style={{ fontSize: "0.95rem" }}>
                            {app["Full Legal Name"] || app["Preferred Name"] || "Unnamed"}
                        </p>
                        <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.78rem", margin: 0 }}>
                            {app.Email || "—"} · {cohort}
                        </p>
                    </div>
                    <span
                        className="fw-semibold"
                        style={{
                            fontSize: "0.7rem",
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                            color: statusColor,
                            backgroundColor: `${statusColor}18`,
                            padding: "3px 10px",
                            borderRadius: "20px",
                        }}
                    >
                        {status}
                    </span>
                </div>
                <div className="d-flex align-items-center gap-3">
                    <span style={{ color: "rgba(255,255,255,0.25)", fontSize: "0.75rem" }}>{submittedAt}</span>
                    <span
                        className="material-icons"
                        style={{ color: "rgba(255,255,255,0.3)", fontSize: "1rem", transition: "transform 0.15s", transform: expanded ? "rotate(180deg)" : "none" }}
                    >
                        expand_more
                    </span>
                </div>
            </div>

            {/* Expanded detail */}
            {expanded && (
                <div
                    style={{ borderTop: "1px solid rgba(255,255,255,0.07)", padding: "20px 24px" }}
                >
                    {/* Meta grid */}
                    <div className="row g-3 mb-4">
                        {[
                            { label: "Grade", value: app["Grade Level"] },
                            { label: "GPA", value: app.GPA },
                            { label: "School", value: app["Academic Institution"] },
                            { label: "Graduation", value: app["Expected Graduation"] },
                            { label: "Guardian", value: app["Guardian Name"] },
                            { label: "Guardian Email", value: app["Guardian Email"] },
                            { label: "Interests", value: Array.isArray(app.Interests) ? app.Interests.join(", ") : app.Interests },
                        ].map(({ label, value }) => value ? (
                            <div key={label} className="col-6 col-md-4">
                                <p style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.07em", margin: "0 0 2px" }}>{label}</p>
                                <p style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.85rem", margin: 0 }}>{value}</p>
                            </div>
                        ) : null)}
                    </div>

                    {/* Essay */}
                    <div
                        className="rounded-3 p-4 mb-4"
                        style={{ backgroundColor: "rgba(55,105,185,0.08)", border: "1px solid rgba(55,105,185,0.2)" }}
                    >
                        <p style={{ color: "#3769B9", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600, marginBottom: "8px" }}>
                            Essay
                        </p>
                        {app["Essay Prompt"] ? (
                            <p
                                className="fw-semibold mb-3"
                                style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.85rem", fontStyle: "italic", lineHeight: 1.5 }}
                            >
                                Prompt: {app["Essay Prompt"]}
                            </p>
                        ) : (
                            <p className="mb-3" style={{ color: "rgba(255,255,255,0.25)", fontSize: "0.82rem", fontStyle: "italic" }}>
                                No prompt selected.
                            </p>
                        )}
                        <p style={{ color: "rgba(255,255,255,0.85)", fontSize: "0.9rem", lineHeight: 1.8, margin: 0, whiteSpace: "pre-wrap" }}>
                            {app.Essay || <span style={{ color: "rgba(255,255,255,0.25)" }}>No response yet.</span>}
                        </p>
                    </div>

                    {/* Other responses */}
                    {[
                        { label: "Why a strong fit?", value: app["Program Fit"] },
                        { label: "Technical Skills", value: app["Technical Skills"] },
                        { label: "Novel problem", value: app["Novel Problem"] },
                        { label: "What makes them an outlier?", value: app.Outlier },
                    ].map(({ label, value }) => value ? (
                        <div key={label} className="mb-3">
                            <p style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.07em", margin: "0 0 4px" }}>{label}</p>
                            <p style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.87rem", lineHeight: 1.75, margin: 0, whiteSpace: "pre-wrap" }}>{value}</p>
                        </div>
                    ) : null)}

                    {/* Portfolio / Video */}
                    <div className="d-flex flex-wrap gap-3 mt-2">
                        {app["Portfolio URL"] && (
                            <a href={app["Portfolio URL"]} target="_blank" rel="noopener noreferrer"
                                className="d-inline-flex align-items-center gap-1 fw-semibold text-decoration-none"
                                style={{ color: "#3769B9", fontSize: "0.82rem" }}>
                                Portfolio <span className="material-icons" style={{ fontSize: "0.9rem" }}>open_in_new</span>
                            </a>
                        )}
                        {app["Video URL"] && (
                            <a href={app["Video URL"]} target="_blank" rel="noopener noreferrer"
                                className="d-inline-flex align-items-center gap-1 fw-semibold text-decoration-none"
                                style={{ color: "#3769B9", fontSize: "0.82rem" }}>
                                Video <span className="material-icons" style={{ fontSize: "0.9rem" }}>open_in_new</span>
                            </a>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────

function Dashboard({ onSignOut }) {
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState("all");

    useEffect(() => {
        getApplications()
            .then(setApps)
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    }, []);

    const filtered = apps.filter(a => {
        const name = (a["Full Legal Name"] || a["Preferred Name"] || "").toLowerCase();
        const email = (a.Email || "").toLowerCase();
        const matchesSearch = !search || name.includes(search.toLowerCase()) || email.includes(search.toLowerCase());
        const matchesFilter =
            filter === "all" ||
            (filter === "complete" && a.officiallySubmitted) ||
            (filter === "incomplete" && !a.officiallySubmitted);
        return matchesSearch && matchesFilter;
    });

    const totalComplete = apps.filter(a => a.officiallySubmitted).length;

    return (
        <div style={{ backgroundColor: "#0a0a0a", minHeight: "100vh", color: "#fff" }}>
            {/* Header */}
            <div style={{ borderBottom: "1px solid rgba(255,255,255,0.07)", padding: "16px 24px" }}
                className="d-flex align-items-center justify-content-between">
                <div>
                    <p className="fw-bold text-white mb-0" style={{ fontSize: "0.95rem" }}>Extern Academy</p>
                    <p style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.75rem", margin: 0 }}>CEO Dashboard · Applications</p>
                </div>
                <button
                    onClick={onSignOut}
                    style={{ background: "none", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.4)", borderRadius: "6px", padding: "6px 14px", fontSize: "0.78rem", cursor: "pointer" }}
                >
                    Sign out
                </button>
            </div>

            <div className="container-fluid px-4 py-4" style={{ maxWidth: "900px", margin: "0 auto" }}>
                {/* Stats */}
                <div className="row g-3 mb-5">
                    {[
                        { label: "Total Applications", value: apps.length },
                        { label: "Complete", value: totalComplete },
                        { label: "In Progress", value: apps.length - totalComplete },
                    ].map(({ label, value }) => (
                        <div key={label} className="col-4">
                            <div className="rounded-3 p-3 text-center" style={{ backgroundColor: "#111", border: "1px solid rgba(255,255,255,0.07)" }}>
                                <p className="fw-bold text-white mb-0" style={{ fontSize: "1.5rem" }}>{value}</p>
                                <p style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.72rem", margin: 0 }}>{label}</p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Filters */}
                <div className="d-flex flex-wrap gap-3 mb-4 align-items-center">
                    <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search by name or email…"
                        style={{
                            flex: 1,
                            minWidth: "200px",
                            backgroundColor: "rgba(255,255,255,0.05)",
                            border: "1px solid rgba(255,255,255,0.1)",
                            borderRadius: "8px",
                            color: "#fff",
                            padding: "10px 14px",
                            fontSize: "0.85rem",
                            outline: "none",
                        }}
                    />
                    <div className="d-flex gap-2">
                        {["all", "complete", "incomplete"].map(f => (
                            <button
                                key={f}
                                onClick={() => setFilter(f)}
                                style={{
                                    backgroundColor: filter === f ? "#3769B9" : "rgba(255,255,255,0.05)",
                                    border: "1px solid " + (filter === f ? "#3769B9" : "rgba(255,255,255,0.1)"),
                                    color: filter === f ? "#fff" : "rgba(255,255,255,0.4)",
                                    borderRadius: "6px",
                                    padding: "8px 14px",
                                    fontSize: "0.78rem",
                                    cursor: "pointer",
                                    textTransform: "capitalize",
                                }}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Application list */}
                {loading && (
                    <p style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.9rem", textAlign: "center", padding: "40px 0" }}>
                        Loading applications…
                    </p>
                )}
                {error && (
                    <p style={{ color: "#D23C1E", fontSize: "0.9rem", textAlign: "center", padding: "40px 0" }}>
                        Error: {error}
                    </p>
                )}
                {!loading && !error && filtered.length === 0 && (
                    <p style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.9rem", textAlign: "center", padding: "40px 0" }}>
                        No applications found.
                    </p>
                )}
                {!loading && !error && filtered.map(app => (
                    <ApplicationCard key={app.id} app={app} />
                ))}
            </div>
        </div>
    );
}

// ── Root export ───────────────────────────────────────────────────────────────

export default function CeoDashboard() {
    const [authed, setAuthed] = useState(() => sessionStorage.getItem(AUTH_KEY) === "1");

    const handleSignOut = () => {
        sessionStorage.removeItem(AUTH_KEY);
        setAuthed(false);
    };

    if (!authed) return <Login onAuth={() => setAuthed(true)} />;
    return <Dashboard onSignOut={handleSignOut} />;
}
